import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart.ts';
import './CartPage.css';
import { submitOrder, fetchItemDetails } from '../services/googleSheets.ts';
import { toast } from 'react-toastify';
import LoadingOverlay from './LoadingOverlay.tsx';
import ImageWithFallback from './ImageWithFallback.tsx';

const CartPage: React.FC = () => {
    const navigate = useNavigate();
    const { cartStore, updateQuantity, removeItem: removeFromCart, clearCart } = useCart();
    const [isLoading, setIsLoading] = useState(false);
    const [itemNames, setItemNames] = useState<{[key: string]: string}>({});

    useEffect(() => {
        const loadItemNames = async () => {
            const names: {[key: string]: string} = {};
            for (const itemId of Object.keys(cartStore)) {
                try {
                    const itemDetails = await fetchItemDetails(itemId);
                    names[itemId] = itemDetails.title;
                } catch (error) {
                    console.error('Error fetching item name:', error);
                    names[itemId] = 'متجر';
                }
            }
            setItemNames(names);
        };
        
        loadItemNames();
    }, [cartStore]);

    const handleCheckout = async (itemId: string) => {
        if (isLoading) return;
        
        setIsLoading(true);
        try {
            const itemCart = cartStore[itemId];
            if (!itemCart) return;

            const orderData = {
                productItems: itemCart.items.map(item => ({
                    title: item.title,
                    quantity: item.quantity,
                    price: item.salePrice || item.mainPrice,
                    total: (item.salePrice || item.mainPrice) * item.quantity
                })),
                totalPrice: itemCart.items.reduce((sum, item) => 
                    sum + (item.salePrice || item.mainPrice) * item.quantity, 0
                ),
                date: new Date().toISOString(),
                whatsappNumber: itemCart.whatsappNumber
            };
            
            await submitOrder(orderData);
            clearCart(itemId);
            setIsLoading(false);
            toast.success('تم إرسال طلبك بنجاح! 🎉', { autoClose: 500 });
        } catch (error) {
            setIsLoading(false);
            toast.error('حدث خطأ أثناء إرسال الطلب. حاول مرة أخرى 😔', { autoClose: 500 });
        }
    };

    return (
        <div className="cart-page">
            {isLoading && <LoadingOverlay />}
            <div className="back-button-container">
                <button onClick={() => navigate(-1)} className="back-button">
                    <svg viewBox="0 0 24 24">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                    <span>رجوع</span>
                </button>
                <button onClick={() => navigate('/')} className="home-button">
                    <svg viewBox="0 0 24 24">
                        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                    </svg>
                    <span>القائمة الرئيسية</span>
                </button>
            </div>
            <div className="cart-container">
                <h2>السلة</h2>
                {Object.entries(cartStore).length > 0 ? (
                    Object.entries(cartStore).map(([itemId, itemCart]) => (
                        <div key={itemId} className="store-cart">
                            <h3>منتجات {itemNames[itemId] || 'المتجر'}</h3>
                            <div className="cart-items">
                                {itemCart.items.map(item => (
                                    <div key={item.id} className="cart-item">
                                        <ImageWithFallback src={item.imageUrl} alt={item.title} />
                                        <div className="item-details">
                                            <h3>{item.title}</h3>
                                            <div className="price">
                                                {item.salePrice ? (
                                                    <>
                                                        <span className="sale-price">{item.salePrice} ج.م</span>
                                                        <span className="original-price">{item.mainPrice} ج.م</span>
                                                    </>
                                                ) : (
                                                    <span className="main-price">{item.mainPrice} ج.م</span>
                                                )}
                                            </div>
                                            <div className="quantity-controls">
                                                <button onClick={() => updateQuantity(itemId, item.id, (item.quantity || 1) + 1)}>+</button>
                                                <span>{item.quantity || 1}</span>
                                                <button onClick={() => updateQuantity(itemId, item.id, (item.quantity || 1) - 1)}>-</button>
                                            </div>
                                        </div>
                                        <button 
                                            className="remove-button"
                                            onClick={() => removeFromCart(itemId, item.id)}
                                        >
                                            حذف
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="cart-summary">
                                <div className="total">
                                    <span>الإجمالي:</span>
                                    <span>{itemCart.items.reduce((sum, item) => 
                                        sum + (item.salePrice || item.mainPrice) * item.quantity, 0
                                    )} ج.م</span>
                                </div>
                                <button 
                                    className="checkout-button"
                                    onClick={() => handleCheckout(itemId)}
                                >
                                    إرسال الطلب عبر واتساب
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-cart">
                        <svg viewBox="0 0 24 24">
                            <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                        <h3>السلة فارغة</h3>
                        <p>لم تقم بإضافة أي منتجات بعد</p>
                        <button onClick={() => navigate('/')}>تسوق الآن</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartPage; 